import "./style.scss";

import CloseIcon from "@mui/icons-material/Close";
import { Avatar, Box, CircularProgress } from "@mui/material";
import { PortalType } from "contexts/portal.context";
import { formatLocaleDateString, getInitials } from "helpers/utils";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getAllArticlesBeneficiars } from "store/articleStore";
import type { IPayment } from "store/paymentSlice";
import { fetchPaymentBeneficiary } from "store/paymentSlice";
import { RootState, useAppDispatch, useAppSelector } from "store/store";
import { EPortalType } from "store/types";
import type { IDatesRange } from "types";

import variables from "../../../assets/styles/_variables.module.scss";
import { EPaymentTableStatus } from "..";
import { bookPaymentsStatus } from "../PaymentTable";
import PressPaymentDetails from "./PressPaymentDetails";
import PublisherPaymentDetails from "./PublisherPaymentDetails";

type TProps = {
    close: () => void;
    payment: IPayment;
    datesRange: IDatesRange | undefined;
};

/**
 * Drawer displaying details of payments (for both press and book)
 */
const PaymentDrawerContent = ({ close, payment, datesRange }: TProps) => {
    const portalType = useContext(PortalType);
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { paymentInfo, paymentInfoStatus } = useAppSelector((state) => state.payment);
    const { articles } = useAppSelector((state) => state.article);

    const articlesConcerned = articles.filter((article) => {
        if (!datesRange) return article.id;
        return (
            (article.paidStatus === 1 || article.paidStatus === 2) &&
            datesRange?.startDate <= new Date(article.paymentDate) &&
            new Date(article.paymentDate) <= datesRange.endDate
        );
    });

    useEffect(() => {
        if (["audiovisual", "press"].includes(portalType) && payment.id) {
            dispatch(getAllArticlesBeneficiars(payment.id));
        }
    }, [portalType, payment]);

    useEffect(() => {
        if (!datesRange) return;
        dispatch(
            fetchPaymentBeneficiary({
                // Convert to date because sometimes it's a string (?)
                startDate: new Date(datesRange.startDate).toLocaleDateString("en-CA"),
                endDate: new Date(datesRange.endDate).toLocaleDateString("en-CA"),
                id: payment.id,
                paymentLotId: payment.paymentLotId,
            }),
        );
    }, [payment]);

    return (
        <div className="payment-details-wrapper">
            {paymentInfoStatus !== "succeeded" ? (
                <Box
                    sx={{
                        height: "100vh",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <CircularProgress sx={{ color: variables["primary-purple"] }} />
                </Box>
            ) : (
                <>
                    <header className="details-header">
                        <div className="details-header-info">
                            <Avatar
                                src={payment.beneficiaryAvatarUrl}
                                sx={{
                                    fontSize: 14,
                                    backgroundColor: variables["beneficiary-avatar-background"],
                                    color: variables["beneficiary-avatar-letter"],
                                }}
                            >
                                {getInitials(payment.name)}
                            </Avatar>
                            <div style={{ display: "flex", flexDirection: "column" }}>
                                <h1 className="details-heading">{payment.name}</h1>
                                {portalType !== EPortalType.BOOK && (
                                    <span style={{ fontSize: "12px", color: "#B3B3B3" }}>
                                        {t("Payment.LastPaymentDate")}{" "}
                                        {formatLocaleDateString({
                                            date: payment.date,
                                            options: { numeric: true, timeZone: "UTC" },
                                        })}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="close-details">
                            <CloseIcon sx={{ color: "#B4B4B4", cursor: "pointer" }} onClick={close} />
                        </div>
                    </header>
                    <main className="details-main" style={{ gap: portalType === EPortalType.BOOK ? "10px" : "20px" }}>
                        {portalType === EPortalType.BOOK ? (
                            <PublisherPaymentDetails
                                paymentData={paymentInfo}
                                beneficiaryName={payment.name}
                                paymentsCanBeDeleted={bookPaymentsStatus(payment) === EPaymentTableStatus.TO_PAY}
                                paymentLotId={payment.paymentLotId}
                                closeDrawer={close}
                            />
                        ) : (
                            <PressPaymentDetails datesRange={datesRange} paymentData={articlesConcerned} />
                        )}
                    </main>
                </>
            )}
        </div>
    );
};

export default PaymentDrawerContent;
